// Response type for GET /incentive_programs/config/current.
// Shape defined by prime-app's program-config.md:
//   https://github.com/Clearcover/prime-app/blob/master/docs/26for26/program-config.md
// Outcomes are nested under config.tracks.TRACK1.phases[] — each phase carries
// its own attempt_type and gameplay outcomes. Fields are snake_case.

export type AttemptType =
  | 'SPIN'
  | 'SCRATCH_OFF'
  | 'RAFFLE_ENTRY'
  | 'TIER_EVAL'
  | 'TOP_PRODUCER_EVAL'
  | 'GRAND_PRIZE_EVAL';

/** One outcome row from the program config. Matches prime's EffectiveConfig.Outcome shape (snake_case). */
export type RawOutcomeConfig = {
  code: string;
  weight: number;
  prize_amount: number | null;
  is_win: boolean;
  max_wins?: number;
  downgrade_to?: string;
  review?: boolean;
  grants_entry_in_phase?: string;
};

export type RawPhaseConfig = {
  code: string;
  attempt_type: AttemptType;
  display_version: string;
  outcomes?: RawOutcomeConfig[];
  drawing?: {
    review_scope: string;
    prizes: Array<{
      code: string;
      win_rate: number;
      prize_amount: number | null;
      max_wins?: number;
      downgrade_to?: string;
      review?: boolean;
      grants_entry_in_phase?: string;
    }>;
  };
};

export type TopProducerEvalConfig = {
  prize_per_phase?: Record<string, number>; // e.g., { "ACT1": 150, "ACT2": 175, "ACT3": 200 }
  // Note: start/end are NOT present in prod config — program window comes from the
  // grand-prize-status endpoint (programStart/programEnd fields, prime PR a9f80bf5).
};

export type TierEvalTierConfig = {
  code: string;
  act_floor: number;
  daily_itd_rate_act1?: number;
  daily_itd_rate_act2_3?: number;
  monthly_rate?: number;
};

export type TierEvalConfig = {
  tiers?: TierEvalTierConfig[];
};

export type Track2Config = {
  top_producer_eval?: TopProducerEvalConfig;
  tier_eval?: TierEvalConfig;
  [key: string]: unknown;
};

export type RawProgramConfig = {
  program: {
    code: string;
    entry_threshold: number;
  };
  tracks: {
    TRACK1: { type: 'PHASED'; phases: RawPhaseConfig[] };
    TRACK2?: Track2Config;
    TRACK3?: Record<string, unknown>;
  };
};

export type GetRewardsConfigResponse = {
  programCode: string;
  phase: {
    number: number;
    code: string;
    name: string;
    start: string;
    end: string;
    attemptType: AttemptType;
  };
  track: string;
  config: RawProgramConfig;
};

/** Internal shape the wheel components consume. Built by `outcomesForPhase`. */
export type OutcomeConfig = {
  /** Outcome code from the program config (e.g. LARGE/MEDIUM/SMALL/CONSOLATION). */
  code: string;
  /** Dollar amount (whole dollars) or null for non-monetary outcomes. */
  prizeAmount: number | null;
  /** Weight used by the backend resolver (relative ratio, not %). */
  weight: number;
  isWin: boolean;
  display: {
    label: string;
    /** Hex color for the wheel segment background. */
    color: string;
    /** Hex color for text/icon rendered on top of the segment. */
    textColor: string;
    /** Copy shown in the outcome modal after the wheel stops. */
    description: string;
    /** Heading shown in the outcome modal. Derived locally for non-win outcomes. */
    modalHeading?: string;
  };
};

type DisplayDefaults = {
  color: string;
  textColor: string;
  description: (amount: number | null) => string;
  modalHeading?: string;
  labelOverride?: string;
  legendLabel: string;
  /** Color for the dot in the prize legend. Defaults to display.color when absent. */
  legendDotColor?: string;
};

// Display metadata the backend config does not carry. Keyed by outcome code so
// the FE renders consistently regardless of prize_amount changes in config.
export const DISPLAY_DEFAULTS_BY_CODE: Record<string, DisplayDefaults> = {
  SUPER: {
    color: '#1a237e',
    textColor: '#ffffff',
    description: (amount) =>
      `$${amount} is on its way to your Awardco account within 5 business days.`,
    legendLabel: 'Super',
    legendDotColor: '#ffd54f',
  },
  LARGE: {
    color: '#1a237e',
    textColor: '#ffffff',
    description: (amount) =>
      `$${amount} is on its way to your Awardco account within 5 business days.`,
    legendLabel: 'Large',
    legendDotColor: '#ffd54f',
  },
  MEDIUM: {
    color: '#3949ab',
    textColor: '#ffffff',
    description: (amount) =>
      `$${amount} is on its way to your Awardco account within 5 business days.`,
    legendLabel: 'Medium',
    legendDotColor: '#3949ab',
  },
  SMALL: {
    color: '#3b52d4',
    textColor: '#ffffff',
    description: (amount) =>
      `$${amount} is on its way to your Awardco account within 5 business days.`,
    legendLabel: 'Small',
    legendDotColor: '#3b52d4',
  },
  CONSOLATION: {
    color: '#4f6ee8',
    textColor: '#ffffff',
    description: () =>
      'You earned a bonus entry for the next Act. Use it when the next Act opens — or play your remaining entries now.',
    modalHeading: '+1 Entry',
    labelOverride: '+1',
    legendLabel: 'Bonus Entry',
    legendDotColor: '#4caf50',
  },
  NONE: {
    color: '#6366f1',
    textColor: '#ffffff',
    description: () =>
      'Keep binding policies to earn more entries and try again!',
    modalHeading: 'No prize this spin',
    labelOverride: 'No prize',
    legendLabel: 'No prize',
    legendDotColor: '#6366f1',
  },
};

const FALLBACK_DISPLAY: DisplayDefaults = {
  color: '#6366f1',
  textColor: '#ffffff',
  description: () => '',
  legendLabel: '',
};

function deriveDisplay(raw: RawOutcomeConfig): OutcomeConfig['display'] {
  const defaults = DISPLAY_DEFAULTS_BY_CODE[raw.code] ?? FALLBACK_DISPLAY;
  const label =
    defaults.labelOverride ??
    (raw.prize_amount != null ? `$${raw.prize_amount}` : raw.code);
  return {
    label,
    color: defaults.color,
    textColor: defaults.textColor,
    description: defaults.description(raw.prize_amount),
    modalHeading: defaults.modalHeading,
  };
}

/**
 * Extracts the gameplay outcomes for the currently active TRACK1 phase and
 * transforms them into the internal OutcomeConfig shape the wheel consumes.
 * Returns an empty array when the matching phase is missing or carries no
 * outcomes (e.g. RAFFLE_ENTRY phases use `drawing.prizes` instead).
 */
export function outcomesForPhase(
  config: GetRewardsConfigResponse,
  phaseCode: string
): OutcomeConfig[] {
  const phase = config.config.tracks.TRACK1.phases.find(
    (p) => p.code === phaseCode
  );
  if (!phase?.outcomes) return [];
  return phase.outcomes.map((raw) => ({
    code: raw.code,
    prizeAmount: raw.prize_amount ?? null,
    weight: raw.weight,
    isWin: raw.is_win,
    display: deriveDisplay(raw),
  }));
}

/** Max prize_amount among winning outcomes in the given phase, or null if none. */
export function maxWinPrizeForPhase(
  config: GetRewardsConfigResponse,
  phaseCode: string
): number | null {
  const amounts = outcomesForPhase(config, phaseCode)
    .filter((o) => o.isWin && o.prizeAmount != null)
    .map((o) => o.prizeAmount as number);
  return amounts.length ? Math.max(...amounts) : null;
}

/**
 * Derives the maximum raffle prize for a RAFFLE_ENTRY phase from
 * `phase.drawing.prizes[*].prize_amount` (max). Used by the raffle cards
 * and confirmation modal for "Win up to $[max]" (AC1, AC2, AC5).
 *
 * Returns null when:
 * - The phase is not found in TRACK1 phases
 * - The phase has no drawing.prizes array
 * - All prize_amounts are null
 */
export function maxRafflePrizeForPhase(
  config: GetRewardsConfigResponse,
  phaseCode: string
): number | null {
  const phase = config.config.tracks.TRACK1.phases.find(
    (p) => p.code === phaseCode
  );
  if (!phase?.drawing?.prizes) return null;
  const amounts = phase.drawing.prizes
    .map((p) => p.prize_amount)
    .filter((a): a is number => a != null);
  return amounts.length ? Math.max(...amounts) : null;
}

/** How many qualifying binds it takes to earn one entry. */
export function entryThresholdFromConfig(
  config: GetRewardsConfigResponse
): number {
  return config.config.program.entry_threshold;
}

/**
 * Returns the top producer prize amount for a given phase code from TRACK2 config.
 * Returns null if TRACK2 is absent, top_producer_eval is missing, or the phase code is not found.
 */
export function topProducerPrizeForPhase(
  config: GetRewardsConfigResponse,
  phaseCode: string
): number | null {
  const track2 = config.config.tracks.TRACK2;
  if (!track2?.top_producer_eval?.prize_per_phase) {
    return null;
  }
  const prize = track2.top_producer_eval.prize_per_phase[phaseCode];
  return prize ?? null;
}

/**
 * Returns the act_floor values for each tier code from TRACK2 tier_eval config.
 * Returns null when TRACK2 or tier_eval.tiers is absent.
 *
 * Tier order in config is Gold→Silver→Bronze (descending act_floor). We map by code
 * so callers don't depend on array position.
 */
export function tierActFloors(
  config: GetRewardsConfigResponse
): { GOLD: number; SILVER: number; BRONZE: number } | null {
  const tiers = config.config.tracks.TRACK2?.tier_eval?.tiers;
  if (!tiers || tiers.length === 0) return null;

  const byCode: Record<string, number> = {};
  for (const t of tiers) {
    byCode[t.code] = t.act_floor;
  }

  const gold = byCode['GOLD'];
  const silver = byCode['SILVER'];
  const bronze = byCode['BRONZE'];

  if (gold == null || silver == null || bronze == null) return null;
  return { GOLD: gold, SILVER: silver, BRONZE: bronze };
}

const NEXT_TIER_CODE: Record<string, 'BRONZE' | 'SILVER' | 'GOLD' | null> = {
  NO_TIER: 'BRONZE',
  BRONZE: 'SILVER',
  SILVER: 'GOLD',
  GOLD: null,
};

/**
 * Returns how many more policies are needed to reach the next tier, using act_floor
 * from program config.
 *
 * NOTE: Tier qualification in prime ALSO requires a daily-ITD-rate threshold. This
 * function intentionally computes the gap from act_floor only (matching AC8's
 * "X more policies" framing). act_floor is necessary-but-not-sufficient; a future
 * reader should not assume meeting act_floor alone guarantees tier advancement.
 *
 * Returns null when:
 * - currentTier is already GOLD (no next tier)
 * - config is absent or tier_eval.tiers is missing
 * - remaining policies would be 0 (already meets or exceeds the threshold)
 */
export function policiesToNextTier(
  currentTier: string,
  actVolumeCount: number,
  config: GetRewardsConfigResponse
): { nextTier: 'BRONZE' | 'SILVER' | 'GOLD'; remaining: number } | null {
  const nextTierCode = NEXT_TIER_CODE[currentTier] ?? null;
  if (nextTierCode === null) return null;

  const floors = tierActFloors(config);
  if (!floors) return null;

  const targetFloor = floors[nextTierCode];
  const remaining = Math.max(0, targetFloor - actVolumeCount);
  if (remaining === 0) return null;

  return { nextTier: nextTierCode, remaining };
}
